import { Captions, HardDrive, MapPinOff, Plus, ShieldCheck, X } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BLOG_ROUTES } from '@/config/blog';
import { BRAND } from '@/config/brand';
import { FAQ_KEYS } from '@/config/faq';
import { APP_PARAMS, COMPARE_PAGES, HUB_PAGES, SITE_PAGES, TOOL_PAGES, type HubKey, type SiteLanguage, type SitePageKey } from '@/config/site-pages';
import { kindOf, type OutputFormatId } from '@/core/project/output-format';
import type { Placement } from '@/core/project/placement';
import type { Preset } from '@/core/project/preset';
import { pickVideoFiles, withViewTransition } from '@/lib/video-files';
import { droppedFiles } from '@/lib/file-handles';
import { takeHandedFiles } from '@/state/handoff';
import { AppHeader } from '../AppHeader';
import { useLanguageName } from '../use-language-name';
import { BlogSection } from './BlogSection';
import { parseAppParams, type CaptionLink } from './app-params';
import { ContinueProjects } from './ContinueProjects';
import { CaptionLinkPanel } from './CaptionLinkPanel';
import { ConvertPanel } from './ConvertPanel';
import { FitPanel } from './FitPanel';
import { InspectPanel } from './InspectPanel';
import { IntentBands } from './IntentBands';
import { RecordCallout } from './RecordCallout';
import { DEFAULT_RECIPE } from './recipe-link';
import { useConsentNotice } from './use-consent-notice';
import { LeftoverRecordings, RecordingProblem } from '../recording/RecordingNotices';
import { AppVersion } from '../AppVersion';

const PROOF = [
  { key: 'device', icon: <HardDrive size={17} /> },
  { key: 'ledger', icon: <ShieldCheck size={17} /> },
  { key: 'location', icon: <MapPinOff size={17} /> },
] as const;

/** What dropped files are for: a recipe to edit with, or (quick convert) only a format to save in. */
export interface FileIntent {
  /** Null when only fitting for a place: a fit link asks for that alone, not the home page's default recipe. */
  readonly preset: Preset | null;
  readonly convertTo: OutputFormatId | null;
  /** Keep the video under this many MB (`?target=20mb`), or null. */
  readonly sizeLimit: number | null;
  /** Fit it for where it will be posted (`?fit=tiktok`), or null. */
  readonly placement: Placement | null;
  /** The captions' language or an English translation (`?lang=tr&task=translate`), or null. */
  readonly captions: CaptionLink | null;
}

export interface HomeScreenProps {
  readonly onFiles: (files: File[], intent: FileIntent) => void;
  /** Status text while files are being read or joined, or false. */
  readonly loading: string | false;
  readonly error?: ReactNode;
}

export function HomeScreen({ onFiles, loading, error }: HomeScreenProps) {
  const { t } = useTranslation();
  useConsentNotice();
  // A content page's button links here with its recipe chosen (`?for=privacy`), a format to convert to (`?to=mp4`) or a place to fit for (`?fit=tiktok`).
  const [params] = useState(() => parseAppParams(location.search));
  // A recording link (`?for=recording`, the screen recorder page) opens the recorder rather than a recipe band.
  const linkedRecording = params.recipe?.builtIn === 'recording';
  const linked = linkedRecording ? null : params.recipe;
  const [active, setActive] = useState<Preset>(linked ?? DEFAULT_RECIPE);
  const [recorder, setRecorder] = useState(linkedRecording);
  const [convertTo, setConvertTo] = useState<OutputFormatId | null>(params.output);
  // Read when files arrive, like `chosen`: a drop right after a change must get the new format.
  const converting = useRef(convertTo);
  const bands = useRef<HTMLDivElement>(null);
  // The view transition applies the new band a frame later; a file chosen right
  // after picking a band must still get that band's recipe.
  const chosen = useRef(active);
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);

  // The linked recipe may be the last band, below the fold on a phone.
  useEffect(() => {
    if (linked) bands.current?.querySelector('[aria-checked="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [linked]);

  const [sizeLimit, setSizeLimit] = useState(params.sizeLimit);
  const limiting = useRef(sizeLimit);
  // The metadata inspector (`?inspect=1`) reads dropped files itself instead of opening them.
  const [inspecting, setInspecting] = useState(params.inspect);
  const checking = useRef(inspecting);
  const [inspected, setInspected] = useState<readonly File[] | null>(null);
  // A captions page's button (`?lang=tr&task=translate`) opens the editor with the captions set up.
  const [captionLink, setCaptionLink] = useState(params.captions);
  const captioning = useRef(captionLink);
  // Without a recipe in the link, the captions get a panel of their own; with one, the recipe's band says it.
  const captionsOnly = captionLink !== null && !linked;
  const dropCaptions = () => {
    captioning.current = null;
    setCaptionLink(null);
    const url = new URL(location.href);
    url.searchParams.delete(APP_PARAMS.captionLanguage);
    url.searchParams.delete(APP_PARAMS.captionTask);
    history.replaceState(history.state, '', url);
  };
  // A spec page's button (`?fit=tiktok`) opens the editor fitted for that place.
  const [placement, setPlacement] = useState(params.placement);
  const fitting = useRef(placement);
  const submit = (files: File[]) => {
    if (checking.current) return setInspected(files);
    // A fit or captions link on its own asks for that alone, not the home page's default recipe.
    const preset = (fitting.current || captioning.current) && !linked ? null : chosen.current;
    onFiles(files, { preset, convertTo: converting.current, sizeLimit: limiting.current, placement: fitting.current, captions: converting.current ? null : captioning.current });
  };
  // A content page that was handed a video became this page; the video goes where its link said
  // (`submit` reads refs only, so the first render's is as good as any).
  useEffect(() => {
    const handed = takeHandedFiles();
    if (handed.length > 0) submit(handed);
  }, []);
  const choosePlacement = (next: Placement | null) => {
    fitting.current = next;
    setPlacement(next);
    // Like the format: reloading or sharing keeps the place, and leaving it drops it from the address.
    const url = new URL(location.href);
    if (next) url.searchParams.set(APP_PARAMS.placement, next.id);
    else url.searchParams.delete(APP_PARAMS.placement);
    history.replaceState(history.state, '', url);
  };
  const leaveInspector = () => {
    checking.current = false;
    withViewTransition(() => setInspecting(false));
    const url = new URL(location.href);
    url.searchParams.delete(APP_PARAMS.inspect);
    history.replaceState(history.state, '', url);
  };
  const dropLimit = () => {
    limiting.current = null;
    setSizeLimit(null);
    const url = new URL(location.href);
    url.searchParams.delete(APP_PARAMS.sizeLimit);
    history.replaceState(history.state, '', url);
  };
  const chooseFormat = (output: OutputFormatId | null) => {
    converting.current = output;
    setConvertTo(output);
    // The address follows, so reloading or sharing keeps the choice (and leaving convert mode drops it).
    const url = new URL(location.href);
    if (output) url.searchParams.set(APP_PARAMS.output, output);
    else url.searchParams.delete(APP_PARAMS.output);
    history.replaceState(history.state, '', url);
    // A size limit only applies to videos.
    if (!output || kindOf(output) !== 'video') dropLimit();
  };
  const select = (preset: Preset) => {
    chosen.current = preset;
    withViewTransition(() => setActive(preset));
  };

  // A file dropped anywhere on the page goes to the chosen recipe.
  const dragHandlers = {
    onDragEnter: (event: DragEvent) => {
      event.preventDefault();
      depth.current++;
      setDragging(true);
    },
    onDragOver: (event: DragEvent) => event.preventDefault(),
    onDragLeave: () => {
      depth.current = Math.max(0, depth.current - 1);
      if (depth.current === 0) setDragging(false);
    },
    onDrop: (event: DragEvent) => {
      event.preventDefault();
      depth.current = 0;
      setDragging(false);
      const files = pickVideoFiles(droppedFiles(event.dataTransfer));
      if (files.length > 0 && !loading) submit(files);
    },
  };

  return (
    <div className="flex min-h-full flex-col" {...dragHandlers}>
      <AppHeader page />
      <main className="mx-auto w-full max-w-300 flex-1 px-5 pt-10 pb-20 sm:px-10 sm:pt-12 lg:px-18">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-16">
          <div>
            {/* The H1 says what this is in the words people search with; the display line asks the question. */}
            <h1 className="mb-4 text-[13px] font-medium text-accent-text sm:mb-5 sm:text-sm">{t('home.heading')}</h1>
            <p className="text-[44px] leading-[0.98] font-semibold tracking-[-0.045em] text-balance text-fg sm:text-[64px] lg:text-[72px]">
              {inspecting
                ? t('home.inspect.display')
                : placement
                  ? t('home.fit.display')
                  : convertTo
                    ? t(sizeLimit ? 'home.convert.compressDisplay' : 'home.convert.display')
                    : captionsOnly
                      ? t('home.captions.display')
                      : t('home.title')}
            </p>
          </div>
          {/* Making a new video sits apart from the recipes for editing one: top right, where the eye lands after the question. */}
          <div className="flex flex-col gap-6 lg:items-end">
            {!recorder && (
              <p className="max-w-110 text-[17px] leading-relaxed text-pretty text-fg-muted lg:order-last lg:mt-auto lg:pb-2">
                {inspecting ? t('home.inspect.subtitle') : placement ? t('home.fit.subtitle') : convertTo ? t('home.convert.subtitle') : captionsOnly ? t('home.captions.subtitle') : t('home.subtitle')}
              </p>
            )}
            {!inspecting && !placement && !convertTo && !captionsOnly && <RecordCallout open={recorder} onOpenChange={setRecorder} loading={loading} />}
          </div>
        </div>

        <div ref={bands} className="mt-8 sm:mt-10">
          {captionLink && !captionsOnly && !inspecting && !convertTo && !placement && <CaptionLinkNote link={captionLink} onDrop={dropCaptions} />}
          <div className="mb-6 space-y-3 empty:hidden">
            <LeftoverRecordings />
            <ContinueProjects />
          </div>
          {inspecting ? (
            <InspectPanel
              files={inspected}
              onFiles={submit}
              // A clean copy is a conversion: written again from the picture and sound, with nothing else carried over.
              onClean={(file, format) => onFiles([file], { preset: chosen.current, convertTo: format, sizeLimit: null, placement: null, captions: null })}
              onEdit={leaveInspector}
              dragging={dragging}
              loading={loading}
            />
          ) : placement ? (
            <FitPanel
              placement={placement}
              recipe={linked}
              onPlacement={choosePlacement}
              onEdit={() => withViewTransition(() => choosePlacement(null))}
              onFiles={submit}
              dragging={dragging}
              loading={loading}
            />
          ) : convertTo ? (
            <ConvertPanel
              output={convertTo}
              sizeLimit={sizeLimit}
              onOutput={chooseFormat}
              onDropLimit={dropLimit}
              onEdit={() => withViewTransition(() => chooseFormat(null))}
              onFiles={submit}
              dragging={dragging}
              loading={loading}
            />
          ) : captionLink && captionsOnly ? (
            <CaptionLinkPanel link={captionLink} onFiles={submit} onEdit={() => withViewTransition(dropCaptions)} dragging={dragging} loading={loading} />
          ) : (
            <IntentBands activeId={active.id} onSelect={select} onFiles={submit} dragging={dragging} loading={loading} />
          )}
        </div>
        {error && <div className="mt-6">{error}</div>}
        <div className="mt-6 empty:hidden">
          <RecordingProblem />
        </div>
        <p className="mt-4 flex items-center gap-2 px-2 text-[13px] text-fg-muted">
          <ShieldCheck size={15} className="shrink-0 text-accent-text" />
          {t('home.privacyNote')}
        </p>

        <section className="mt-24 grid gap-8 border-t border-line-strong pt-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-16" aria-labelledby="proof-title">
          <h2 id="proof-title" className="text-[28px] leading-tight font-semibold tracking-[-0.03em] text-fg">
            {t('home.proofTitle')}
          </h2>
          <dl className="divide-y divide-line-strong">
            {PROOF.map(({ key, icon }) => (
              <div key={key} className="grid gap-1.5 py-5 first:pt-0 sm:grid-cols-[220px_minmax(0,1fr)] sm:gap-8">
                <dt className="flex items-center gap-2.5 text-[15px] font-semibold text-fg">
                  <span className="text-accent-text">{icon}</span>
                  {t(`home.proof.${key}.title`)}
                </dt>
                <dd className="text-[15px] leading-relaxed text-fg-muted">{t(`home.proof.${key}.body`)}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="mt-20 grid gap-8 border-t border-line-strong pt-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-16" aria-labelledby="faq-title">
          <h2 id="faq-title" className="text-[28px] leading-tight font-semibold tracking-[-0.03em] text-fg">
            {t('home.faq.title')}
          </h2>
          {/* Closed answers stay in the page, so search engines read them too. */}
          <div className="divide-y divide-line-strong">
            {FAQ_KEYS.map((key) => (
              <details key={key} className="group py-1 first:-mt-3">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-lg py-4 text-[15px] font-semibold text-fg outline-none focus-visible:ring-2 focus-visible:ring-accent [&::-webkit-details-marker]:hidden">
                  {t(`home.faq.items.${key}.q`)}
                  <Plus size={17} className="shrink-0 text-fg-subtle transition-transform duration-200 ease-out-soft group-open:rotate-45" aria-hidden />
                </summary>
                <p className="max-w-160 pb-5 text-[15px] leading-relaxed text-fg-muted">{t(`home.faq.items.${key}.a`)}</p>
              </details>
            ))}
          </div>
        </section>

        <BlogSection />
      </main>
      <footer className="border-t border-line">
        <SiteLinks />
        <div className="mx-auto flex max-w-300 flex-col items-center justify-between gap-3 px-5 pt-6 pb-24 text-[13px] text-fg-subtle sm:flex-row sm:px-10 sm:pb-6 lg:px-18">
          <span className="text-center sm:text-left">
            © {new Date().getFullYear()} {BRAND.name} · {t('home.footer')}
          </span>
          <AppVersion className="sm:order-last" />
          {BRAND.promoLinks.length > 0 && (
            <nav className="flex items-center gap-4" aria-label={t('nav.moreProjects')}>
              <span>{t('nav.moreProjects')}:</span>
              {BRAND.promoLinks.map((link) => (
                <a key={link.href} href={link.href} target="_blank" rel="noopener" className="text-fg-muted hover:text-fg">
                  {link.label}
                </a>
              ))}
            </nav>
          )}
        </div>
      </footer>
    </div>
  );
}

/** What a link set up for the captions, before any file is dropped, with a way to drop it. */
function CaptionLinkNote({ link, onDrop }: { readonly link: CaptionLink; readonly onDrop: () => void }) {
  const { t } = useTranslation();
  const name = useLanguageName();
  const language = link.language && name(link.language);
  const text = link.task ? (language ? t('home.captionLink.translateFrom', { language }) : t('home.captionLink.translate')) : t('home.captionLink.language', { language });
  return (
    <p className="mb-4 inline-flex min-h-8 items-center gap-2 rounded-lg bg-elevated py-1 pr-1 pl-2.5 text-[13px] font-medium text-fg ring-1 ring-line ring-inset">
      <Captions size={14} className="shrink-0 text-accent-text" aria-hidden />
      {text}
      <button
        type="button"
        onClick={onDrop}
        aria-label={t('home.captionLink.drop')}
        title={t('home.captionLink.drop')}
        className="flex size-6 items-center justify-center rounded-md text-fg-muted outline-none hover:bg-panel hover:text-fg focus-visible:ring-2 focus-visible:ring-accent"
      >
        <X size={13} />
      </button>
    </p>
  );
}

/** Every content page, so visitors and search engines find them from the app. */
function SiteLinks() {
  const { t, i18n } = useTranslation();
  const language = i18n.language as SiteLanguage;
  return (
    <div className="mx-auto max-w-300 px-5 sm:px-10 lg:px-18">
      <div className="grid gap-8 border-b border-line py-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,1fr)] lg:gap-16">
        <LinkGroup title={t('site.toolsTitle')} keys={TOOL_PAGES} columns="min-[420px]:grid-cols-2 md:grid-cols-3">
          {/* The programmatic pages' hubs, after the tools as in every content page's footer. */}
          {(Object.keys(HUB_PAGES) as HubKey[]).map((key) => (
            <SiteLink key={key} href={HUB_PAGES[key][language]}>
              {t(`site.hubs.${key}`)}
            </SiteLink>
          ))}
        </LinkGroup>
        <LinkGroup title={t('site.compareTitle')} keys={[...COMPARE_PAGES, 'privacy']} columns="min-[420px]:grid-cols-2 lg:grid-cols-1">
          {/* The Worker redirects /blog to wherever the blog lives, so this link never goes stale. */}
          <SiteLink href={BLOG_ROUTES.home}>{t('site.blog')}</SiteLink>
        </LinkGroup>
      </div>
    </div>
  );
}

function LinkGroup({ title, keys, columns, children }: { readonly title: string; readonly keys: readonly SitePageKey[]; readonly columns: string; readonly children?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language as SiteLanguage;
  return (
    <nav aria-label={title}>
      <p className="mb-3 text-[13px] font-medium text-fg-subtle">{title}</p>
      <ul className={`grid grid-cols-1 gap-x-8 gap-y-2.5 text-sm ${columns}`}>
        {keys.map((key) => (
          <SiteLink key={key} href={SITE_PAGES[key][language]}>
            {t(`site.links.${key}`)}
          </SiteLink>
        ))}
        {children}
      </ul>
    </nav>
  );
}

function SiteLink({ href, children }: { readonly href: string; readonly children: ReactNode }) {
  return (
    <li>
      <a href={href} className="rounded-md text-fg-muted transition-colors duration-200 ease-out-soft outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-accent">
        {children}
      </a>
    </li>
  );
}
